import {
  ArrowLeftIcon,
  ArrowRightIcon,
  FeatherIcon,
  XIcon,
} from "lucide-react";

import {
  ActivityEventRow,
  ActivityQuote,
  EventIconCircle,
  UserName,
} from "./ActivityEventRow";

import { RichTextDisplay, Text } from "@/components";
import { Avatar } from "@/components/quick-access/Avatar";

import { useGetUser } from "@/hooks/queries/useGetUser";
import {
  activityTypography,
  candidacyStatusToLabel,
  candidacyStatusToRank,
} from "@/lib/constants";
import { CandidacyStatus, type CandidacyStatusChange } from "@/types";

export const StatusChangeEvent = ({
  candidateFullName,
  currentUserId,
  statusChange,
}: {
  candidateFullName: string;
  currentUserId: number;
  statusChange: CandidacyStatusChange;
}) => {
  /* Database CRUD Operations */
  // Read
  const { data: user } = useGetUser({ userId: statusChange.userId });

  /* Helpers */
  const isDirectToApplied =
    !statusChange.fromStatus &&
    statusChange.toStatus === CandidacyStatus.APPLIED;
  const isDirectToShortlist =
    !statusChange.fromStatus &&
    statusChange.toStatus === CandidacyStatus.SHORTLIST;
  const isResurrected =
    statusChange.byAgent &&
    statusChange.fromStatus === CandidacyStatus.REJECTED &&
    statusChange.toStatus === CandidacyStatus.APPLIED;
  const isRejected = statusChange.toStatus === CandidacyStatus.REJECTED;
  const isEmploymentEnded =
    statusChange.toStatus === CandidacyStatus.EMPLOYMENT_ENDED;
  const isBackward =
    !!statusChange.fromStatus &&
    candidacyStatusToRank[statusChange.fromStatus] >
      candidacyStatusToRank[statusChange.toStatus];
  const candidateEmphasis = (
    <span className={activityTypography.emphasis}>{candidateFullName}</span>
  );

  /* Render */
  const renderIcon = () => {
    if (isResurrected) {
      return (
        <EventIconCircle className="bg-sky-600">
          <FeatherIcon className="h-3 w-3 text-white" />
        </EventIconCircle>
      );
    }
    if (isDirectToApplied) {
      const initials = candidateFullName
        .split(" ")
        .map((namePart) => namePart[0] || "")
        .join("");
      return (
        <EventIconCircle className="bg-green-700">
          <Text className="text-[10px] font-medium text-white">{initials}</Text>
        </EventIconCircle>
      );
    }
    if (isDirectToShortlist) {
      return (
        <Avatar
          initials={user ? `${user.firstName[0]}${user.lastName[0]}` : "?"}
        />
      );
    }
    if (isRejected || isEmploymentEnded) {
      return (
        <EventIconCircle className="bg-red-600">
          <XIcon className="h-3 w-3 stroke-[3px] text-white" />
        </EventIconCircle>
      );
    }
    if (isBackward) {
      return (
        <EventIconCircle className="bg-slate-500">
          <ArrowLeftIcon className="h-3 w-3 stroke-[3px] text-white" />
        </EventIconCircle>
      );
    }
    return (
      <EventIconCircle className="bg-green-700">
        <ArrowRightIcon className="h-3 w-3 stroke-[3px] text-white" />
      </EventIconCircle>
    );
  };

  const renderSentence = () => {
    const toStatusLabel = candidacyStatusToLabel[statusChange.toStatus];

    if (isResurrected) {
      return <>{candidateEmphasis} was resurrected by a Stevie call</>;
    }
    if (isDirectToApplied) {
      return <>{candidateEmphasis} applied</>;
    }
    if (isDirectToShortlist) {
      return (
        <>
          <UserName currentUserId={currentUserId} user={user} /> shortlisted{" "}
          {candidateEmphasis}
        </>
      );
    }
    if (isRejected) {
      return (
        <>
          <UserName currentUserId={currentUserId} user={user} /> rejected{" "}
          {candidateEmphasis}
        </>
      );
    }
    if (isEmploymentEnded) {
      return (
        <>
          <UserName currentUserId={currentUserId} user={user} /> moved{" "}
          {candidateEmphasis} to employment ended
        </>
      );
    }
    if (statusChange.fromStatus) {
      return (
        <>
          <UserName currentUserId={currentUserId} user={user} /> moved{" "}
          {candidateEmphasis} from{" "}
          {candidacyStatusToLabel[statusChange.fromStatus]} to {toStatusLabel}
        </>
      );
    }
    return (
      <>
        <UserName currentUserId={currentUserId} user={user} /> moved{" "}
        {candidateEmphasis} to {toStatusLabel}
      </>
    );
  };

  const render = () => {
    return (
      <ActivityEventRow
        byAgent={statusChange.byAgent}
        createdAt={statusChange.createdAt}
        icon={renderIcon()}
        quote={
          (isRejected || isEmploymentEnded) &&
          statusChange.statusChangeReason ? (
            <ActivityQuote>
              <RichTextDisplay richText={statusChange.statusChangeReason} />
            </ActivityQuote>
          ) : null
        }
      >
        {renderSentence()}
      </ActivityEventRow>
    );
  };

  return render();
};
